import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../../../utils/firebase";
import {
    setInviteLinkUserDetails,
    respondToUser,
    validateInviteCode
} from "../../../app/store/slices/event/invitation";

export default function useVerifyInviteCode() {

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const { inviteCode } = useParams();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);


    /**
     * Get the actual invite code by removing the "code=" prefix if it exists
     */
    const userHasInviteCode =
        inviteCode?.startsWith("code=") ? inviteCode.slice(5) : inviteCode;


    /**
     * useEffect to verify the invite code when the component mounts or when the invite code changes
     * 
     * Steps:
     * 1. Check if the user has an invite code
     * 2. Dispatch the validateInviteCode action to verify the invite code
     * 3. If valid, store the session ID and Firebase token (if provided)
     * 4. Sign in to Firebase with the custom token (if provided)
     * 5. Dispatch the respondToUser action to notify the server
     * 6. Navigate the user based on their role (staff or attendee)
     * 7. Handle errors and set the error state accordingly
     * 
     */
    useEffect(() => {

        async function verifyCode() {

            // Step 1
            if (!userHasInviteCode) {
                return;
            }

            try {
                setLoading(true);

                // Step 2
                const verifyInviteCode = await dispatch(validateInviteCode(userHasInviteCode)).unwrap();

                const verifyInviteCodeResponse = {
                    data: verifyInviteCode?.data || null,
                    code: verifyInviteCode?.code || null,
                    message: verifyInviteCode?.message || null,
                };

                // Step 3
                const sessionId = verifyInviteCodeResponse?.data?.session_id;
                localStorage.setItem("sessionId", sessionId);

                const firebaseToken = verifyInviteCodeResponse?.data?.firebase_token || null;


                let roleResponse;


                // Step 4
                if (firebaseToken) {

                    await signInWithCustomToken(auth, firebaseToken);

                    // Step 5
                    const notifyServer = await dispatch(respondToUser({ sessionId })).unwrap();

                    roleResponse = {
                        data: notifyServer?.data || null,
                        code: notifyServer?.code || null,
                        message: notifyServer?.message || null,
                    };
                } else {
                    const notifyServer = await dispatch(respondToUser({ sessionId })).unwrap();

                    roleResponse = {
                        data: notifyServer?.data || null,
                        code: notifyServer?.code || null,
                        message: notifyServer?.message || null,
                    };
                }


                // Step 6
                if (roleResponse?.data?.type === "staff") {
                    navigate(`/pages/event-attendance/scanning`, { replace: true });
                    // navigate(`/pages/${verifyInviteCodeResponse?.data?.event_slug}/scanning`, { replace: true });
                }
                else {
                    dispatch(setInviteLinkUserDetails(roleResponse?.data || null));
                    navigate(`/e/${verifyInviteCodeResponse?.data?.event_slug}`, { replace: true });
                }


            } catch (error) {
                // console.error("Error verifying invite code:", error);
                // Step 7
                if (error.status === 403 && error.data?.data?.status !== "scheduled") {
                    navigate(`/e/${error?.data?.data?.slug}`, { replace: true });
                    dispatch(setInviteLinkUserDetails({
                        errorText: error.data?.data?.message,
                        status: error.data?.data?.status
                    }));
                    return;
                }

                setError({
                    code: error?.code || 500,
                    message: error?.message || null,
                    data: error?.data || null,
                })
            } finally {
                setLoading(false);
            }
        }

        verifyCode();
    }, [dispatch, userHasInviteCode]);


    return {
        loading,
        error
    }
}